/**
 * Calcule la luminance relative d'une couleur RGB.
 * La luminance est une mesure de la luminosité perçue d'une couleur.
 *
 * @param {string} color - Une chaîne représentant une couleur RGB, par exemple "rgb(255, 255, 255)".
 * @returns {number} La luminance relative de la couleur, un nombre entre 0 (noir) et 1 (blanc).
 */
export const getLuminance = (color) => {
  // Supposons que color est une chaîne RGB, par exemple "rgb(255, 255, 255)"
  const rgb = color.match(/\d+/g).map(Number)
  // Calcul de la luminance relative selon la formule standard
  const [r, g, b] = rgb.map((c) => {
    const cs = c / 255
    return cs <= 0.03928 ? cs / 12.92 : Math.pow((cs + 0.055) / 1.055, 2.4)
  })
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

export const isBackgroundImage = (element) => {
  if (element.classList.contains('has-image')) {
    return true
  }
  const bgImage = window.getComputedStyle(element).backgroundImage
  return bgImage && bgImage !== 'none'
}

/**
 * Définit la couleur du texte (noir ou blanc) d'un élément du menu
 * en fonction de la couleur de fond de la section associée.
 * @param {HTMLElement} navLink - Lien de navigation correspondant à l'élément de menu.
 * @param {string} bgColor - Couleur de fond de la section.
 * @param {HTMLElement} section - Section associée pour vérifier l'image de fond.
 */
export const setTextColorBasedOnBg = (navLink, bgColor, section) => {
  if (isBackgroundImage(section)) {
    navLink.style.color = 'white'
  } else if (
    !bgColor ||
    bgColor === 'rgba(0, 0, 0, 0)' ||
    bgColor === 'transparent'
  ) {
    navLink.style.color = 'black'
  } else {
    const luminance = getLuminance(bgColor)
    navLink.style.color = luminance < 0.5 ? 'white' : 'black'
  }
}

/**
 * Retourne une classe de texte (text-white ou text-black) en fonction de la couleur de fond.
 * @param {string} bgColor - Couleur de fond de la section.
 * @param {HTMLElement} section - Section associée pour vérifier l'image de fond.
 * @returns {string} La classe de couleur à appliquer sur le texte.
 */
export const getTextColorBasedOnBg = (bgColor, section = null) => {
  if (!bgColor) {
    bgColor = '#FFFFFF'
  }

  if (section && isBackgroundImage(section)) {
    return 'text-white'
  } else if (
    !bgColor ||
    bgColor === 'rgba(0, 0, 0, 0)' ||
    bgColor === 'transparent'
  ) {
    return 'text-black'
  } else {
    if (bgColor.startsWith('#')) {
      bgColor = hexToRGB(bgColor)
    }

    const luminance = getLuminance(bgColor)
    return luminance < 0.5 ? 'text-white' : 'text-black'
  }
}

/**
 * Convertit une couleur hexadécimale en une chaîne RGB ou RGBA.
 *
 * @param {string} hex - Code couleur hexadécimal (par exemple, "#ff00ff").
 * @param {number} [alpha] - Valeur optionnelle d'opacité.
 * @returns {string} La couleur au format RGB ou RGBA.
 */
export const hexToRGB = (hex, alpha) => {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)

  if (alpha) {
    return `rgba(${r}, ${g}, ${b}, ${alpha})`
  }

  return `rgb(${r}, ${g}, ${b})`
}
